import React, { useState } from "react";

type DownloadButtonProps = {
  url: string;
  filename?: string;
  headers?: Record<string, string>;
};

export default function DownloadButton({
  url,
  filename = "video.mp4",
  headers,
}: DownloadButtonProps) {
  const [progress, setProgress] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setDownloading(true);
    setError(null);
    setProgress(null);

    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error("cant download rus audio");
      }
      const total = Number(res.headers.get("content-length")) || 0;

      if (!res.body || !("getReader" in res.body)) {
        // Fallback for older browsers
        const blob = await res.blob();
        triggerSave(blob, filename);
        setDownloading(false);
        return;
      }

      const reader = res.body.getReader();
      const chunks: Uint8Array[] = [];
      let received = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          received += value.length;
          if (total) {
            setProgress(Math.round((received / total) * 100));
          }
        }
      }

      const blob = new Blob(chunks, {
        type: res.headers.get("content-type") ?? "application/octet-stream",
      });

      triggerSave(blob, filename);
    } catch (e: any) {
      setError(e?.message ?? "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <button onClick={handleDownload} disabled={downloading}>
        {downloading ? `Downloading${progress !== null ? ` ${progress}%` : "..."}` : "Download"}
      </button>

      {progress !== null && (
        <div
          style={{
            height: 6,
            width: 200,
            background: "#eee",
            marginTop: 8,
            position: "relative",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "#3b82f6",
              transition: "width 0.2s",
            }}
          />
        </div>
      )}

      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
}

function triggerSave(blob: Blob, filename: string) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}
